<section class="quorum" aria-label="Synthetic quorum calculator">
  <div class="quorum__header">
    <div>
      <h4>Quorum calculator</h4>
      <p>
        Try explicit platform rules with sample members and votes. Inputs are
        not saved to this workflow. No signed decision is created.
      </p>
    </div>
    <button
      uiButton
      type="button"
      (click)="loadExample()"
      [disabled]="controlsDisabled"
    >
      Load synthetic example
    </button>
  </div>

  <p *ngIf="rulesLoading" role="status">Loading available rules…</p>
  <p *ngIf="rulesError" class="quorum__error" role="alert">
    {{ rulesError }}
    <button uiButton type="button" (click)="loadRules()">Retry rules</button>
  </p>

  <fieldset class="quorum__settings" [disabled]="controlsDisabled">
    <legend>Sample policy</legend>
    <label
      >Quorum threshold
      <input
        uiControl
        type="number"
        min="1"
        [max]="rules?.maximumMembers ?? 256"
        step="1"
        placeholder="Choose threshold"
        [(ngModel)]="quorumThreshold"
        (ngModelChange)="inputsChanged()"
      />
    </label>
    <label
      >Approval rule
      <select
        uiControl
        [(ngModel)]="approvalRuleCode"
        (ngModelChange)="inputsChanged()"
      >
        <option value="">Choose approval rule</option>
        <option *ngFor="let rule of rules?.approvalRules" [value]="rule.value">
          {{ rule.label }}
        </option>
      </select>
    </label>
    <label
      >Approval denominator
      <select
        uiControl
        [(ngModel)]="denominatorCode"
        (ngModelChange)="inputsChanged()"
      >
        <option value="">Choose denominator</option>
        <option *ngFor="let rule of rules?.denominators" [value]="rule.value">
          {{ rule.label }}
        </option>
      </select>
    </label>
    <label
      >Abstentions toward quorum
      <select
        uiControl
        [(ngModel)]="abstentionQuorumCode"
        (ngModelChange)="inputsChanged()"
      >
        <option value="">Choose abstention rule</option>
        <option
          *ngFor="let rule of rules?.abstentionQuorumRules"
          [value]="rule.value"
        >
          {{ rule.label }}
        </option>
      </select>
    </label>
    <label
      >Tied approval and rejection votes
      <select uiControl [(ngModel)]="tieCode" (ngModelChange)="inputsChanged()">
        <option value="">Choose tie rule</option>
        <option *ngFor="let rule of rules?.tieRules" [value]="rule.value">
          {{ rule.label }}
        </option>
      </select>
    </label>
    <p class="quorum__hint">
      Votes cast includes approval and rejection votes only. The abstention rule
      affects quorum participation.
    </p>
  </fieldset>

  <fieldset class="quorum__members" [disabled]="controlsDisabled">
    <legend>Sample members and votes</legend>
    <div
      class="quorum__member"
      *ngFor="let member of members; let index = index"
    >
      <label
        >Member {{ index + 1 }}
        <input
          uiControl
          type="text"
          [(ngModel)]="member.name"
          (ngModelChange)="inputsChanged()"
          autocomplete="off"
          maxlength="128"
          placeholder="Sample name"
        />
      </label>
      <label
        >Vote
        <select
          uiControl
          [(ngModel)]="member.choice"
          (ngModelChange)="inputsChanged()"
          [attr.aria-label]="'Vote for member ' + (index + 1)"
        >
          <option value="approve">Approve</option>
          <option value="reject">Reject</option>
          <option value="abstain">Abstain</option>
          <option value="missing">Missing</option>
        </select>
      </label>
      <button
        uiButton
        type="button"
        (click)="removeMember(index)"
        [disabled]="members.length <= 1"
        [attr.aria-label]="'Remove member ' + (index + 1)"
      >
        Remove
      </button>
    </div>
    <div>
      <button
        uiButton
        type="button"
        (click)="addMember()"
        [disabled]="members.length >= (rules?.maximumMembers ?? 256)"
      >
        Add member
      </button>
    </div>
    <p class="quorum__hint">
      Missing means no ballot was supplied. Each listed member is eligible in
      this sample.
    </p>
  </fieldset>

  <div class="quorum__actions">
    <button
      uiButton
      type="button"
      severity="primary"
      [outlined]="false"
      (click)="calculate()"
      [disabled]="controlsDisabled || loading"
    >
      Calculate sample
    </button>
    <span *ngIf="loading" role="status">Calculating…</span>
  </div>
  <p *ngIf="error" class="quorum__error" role="alert">{{ error }}</p>

  <section
    *ngIf="result as calculation"
    class="quorum__result"
    aria-label="Sample calculation result"
    aria-live="polite"
  >
    <strong>{{ outcomeLabel }}</strong>
    <p>
      Quorum {{ calculation.quorumSatisfied ? "met" : "not met" }} for this
      sample.
    </p>
    <dl>
      <div>
        <dt>Eligible members</dt>
        <dd>{{ calculation.eligibleCount }}</dd>
      </div>
      <div>
        <dt>Counted for quorum</dt>
        <dd>{{ calculation.participatingCount }}</dd>
      </div>
      <div>
        <dt>Approve / reject</dt>
        <dd>{{ calculation.approveCount }} / {{ calculation.rejectCount }}</dd>
      </div>
      <div>
        <dt>Abstain / missing</dt>
        <dd>{{ calculation.abstainCount }} / {{ calculation.missingCount }}</dd>
      </div>
      <div>
        <dt>Approval denominator</dt>
        <dd>{{ calculation.voteDenominator }}</dd>
      </div>
      <div>
        <dt>Approvals required</dt>
        <dd>{{ calculation.requiredApprovals ?? "No decisive votes" }}</dd>
      </div>
    </dl>
    <ul *ngIf="calculation.reasonCodes.length">
      <li *ngFor="let reason of calculation.reasonCodes">
        {{ reasonLabel(reason) }}
      </li>
    </ul>
    <p class="quorum__hint">
      This calculation describes these sample inputs only. It does not record
      votes, satisfy a live workflow gate or finalize a decision.
    </p>
  </section>
</section>
